import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { concatMap, filter, finalize, from, map, Observable, of, shareReplay, Subject, tap, toArray } from 'rxjs';
import { MainService } from '../main.service';
import { Store } from '../../types';
import { differenceInMinutes } from 'date-fns';

export interface CreateStoreRequest {
  id: number;
  place: string;
  district: string;
  address?: string;
  signature: string;
}

@Injectable({ providedIn: 'root' })
export class StoreService {
  private readonly storesChanged = new Subject<void>();
  readonly storesChanged$ = this.storesChanged.asObservable();

  private readonly cacheTtlMinutes = 5;
  private allStores: Store[] = [];
  private loadedAt: Date = new Date(0);
  private readonly existingStoreCache = new Map<number, Store>();
  private existingStoresLoadedAt: Date = new Date(0);
  private resolving?: Observable<Store[]>;
  private cacheIdentity = '';
  private generation = 0;

  constructor(private http: HttpClient, private mainService: MainService) {}

  getStoreById(storeId: number): Observable<Store | null> {
    this.checkIdentity();
    this.expireExistingStoreCache();
    const cached = this.existingStoreCache.get(storeId);
    if (cached) return of(cached);
    const generation = this.generation;
    return this.http.get<Store>(`${this.mainService.getApiURL()}Store/Stores/${storeId}`, {
      headers: this.mainService.getHeaders(),
    }).pipe(tap(store => { if (generation === this.generation) this.existingStoreCache.set(store.id, store); }));
  }

  resolveExistingStores(ids: number[]): Observable<Store[]> {
    this.checkIdentity();
    this.expireExistingStoreCache();
    const uniqueIds = [...new Set(ids)];
    const missing = uniqueIds.filter(id => !this.existingStoreCache.has(id));
    if (!missing.length) return of(uniqueIds.map(id => this.existingStoreCache.get(id)!).filter(Boolean));
    const chunks: number[][] = [];
    const generation = this.generation;
    for (let index = 0; index < missing.length; index += 500) chunks.push(missing.slice(index, index + 500));
    return from(chunks).pipe(concatMap(chunk => {
      const query = chunk.map(id => `ids=${encodeURIComponent(id)}`).join('&');
      return this.http.get<Store[]>(`${this.mainService.getApiURL()}Store/Existing?${query}`, {
        headers: this.mainService.getHeaders(),
      });
    }), toArray(), map(responses => {
      if (generation !== this.generation) return [];
      responses.flat().forEach(store => this.existingStoreCache.set(store.id, store));
      this.existingStoresLoadedAt = new Date();
      return uniqueIds.map(id => this.existingStoreCache.get(id)).filter((store): store is Store => !!store);
    }));
  }

  getAllStores(forceRefresh = false): Observable<Store[]> {
    this.checkIdentity();
    if (!forceRefresh && differenceInMinutes(new Date(), this.loadedAt) <= this.cacheTtlMinutes) return of(this.allStores);
    if (this.resolving && !forceRefresh) return this.resolving;
    let request!: Observable<Store[]>;
    const generation = this.generation;
    request = this.http.get<Store[]>(`${this.mainService.getApiURL()}Store/Stores`, {
      headers: this.mainService.getHeaders(),
    }).pipe(filter(() => {
      this.checkIdentity();
      return generation === this.generation;
    }), tap(stores => {
      this.allStores = stores;
      this.loadedAt = new Date();
    }), finalize(() => { if (this.resolving === request) this.resolving = undefined; }), shareReplay({ bufferSize: 1, refCount: true }));
    this.resolving = request;
    return request;
  }

  updateStoreSignature(storeId: number, signature: string): Observable<boolean> {
    const identity = this.mainService.getSessionIdentity();
    return this.http.put<boolean>(`${this.mainService.getApiURL()}Store/Stores/${storeId}/:update-signature`, { signature }, {
      headers: this.mainService.getHeaders(),
    }).pipe(tap(success => { if (success) this.cacheMutation(undefined, identity); }));
  }

  createStore(request: CreateStoreRequest): Observable<Store> {
    const identity = this.mainService.getSessionIdentity();
    return this.http.post<Store>(`${this.mainService.getApiURL()}Store/Stores`, request, {
      headers: this.mainService.getHeaders(),
    }).pipe(tap(store => this.cacheMutation(store, identity)));
  }

  cacheMutation(store: Store | undefined, requestingIdentity?: string): void {
    this.checkIdentity();
    if (requestingIdentity !== undefined && requestingIdentity !== this.cacheIdentity) return;
    this.invalidateCaches();
    this.existingStoresLoadedAt = new Date();
    if (store) this.existingStoreCache.set(store.id, store);
    this.storesChanged.next();
  }

  invalidateCaches(): void {
    this.generation++;
    this.resolving = undefined;
    this.loadedAt = new Date(0);
    this.allStores = [];
    this.existingStoreCache.clear();
    this.existingStoresLoadedAt = new Date(0);
  }

  private checkIdentity(): void {
    const identity = this.mainService.getSessionIdentity();
    if (identity !== this.cacheIdentity) {
      this.cacheIdentity = identity;
      this.invalidateCaches();
    }
  }

  private expireExistingStoreCache(): void {
    if (differenceInMinutes(new Date(), this.existingStoresLoadedAt) > this.cacheTtlMinutes) {
      this.existingStoreCache.clear();
      this.existingStoresLoadedAt = new Date();
    }
  }
}
